import { commonText, useCommonLanguage } from "../../i18n/commonPresentation";
import type { ReactNode } from "react";
import { Icon } from "./Icon";
import { cn } from "../../utils/cn";

type AlertVariant = "default" | "success" | "warning" | "error" | "info";

type AlertProps = {
  title?: string;
  children?: ReactNode;
  icon?: ReactNode;
  variant?: AlertVariant;
  className?: string;
  /** Renders an X button in the top-right corner of the alert. */
  dismissible?: boolean;
  /** Called when the dismiss button is clicked. */
  onDismiss?: () => void;
};

const variantClasses: Record<
  AlertVariant,
  { container: string; title: string; body: string; icon: string }
> = {
  default: {
    container: "border-border/60 bg-card/70 text-foreground",
    title: "text-foreground",
    body: "text-muted-foreground",
    icon: "text-muted-foreground"
  },
  success: {
    container:
      "border-emerald-500/30 bg-emerald-500/10 text-emerald-900 [.theme-smoked_&]:border-emerald-500/30 [.theme-smoked_&]:bg-emerald-500/15 [.theme-smoked_&]:text-emerald-50 [.theme-spectre_&]:border-emerald-500/30 [.theme-spectre_&]:bg-emerald-500/15 [.theme-spectre_&]:text-emerald-50 [.theme-reel_&]:border-emerald-500/30 [.theme-reel_&]:bg-emerald-500/15 [.theme-reel_&]:text-emerald-50",
    title:
      "text-emerald-900 [.theme-smoked_&]:text-emerald-50 [.theme-spectre_&]:text-emerald-50 [.theme-reel_&]:text-emerald-50",
    body:
      "text-emerald-800/90 [.theme-smoked_&]:text-emerald-100 [.theme-spectre_&]:text-emerald-100 [.theme-reel_&]:text-emerald-100",
    icon:
      "text-emerald-600 [.theme-smoked_&]:text-emerald-300 [.theme-spectre_&]:text-emerald-300 [.theme-reel_&]:text-emerald-300"
  },
  warning: {
    container:
      "border-amber-500/50 bg-amber-200/70 text-amber-950 [.theme-smoked_&]:border-amber-500/40 [.theme-smoked_&]:bg-amber-500/25 [.theme-smoked_&]:text-amber-50 [.theme-spectre_&]:border-amber-500/40 [.theme-spectre_&]:bg-amber-500/25 [.theme-spectre_&]:text-amber-50 [.theme-reel_&]:border-amber-500/40 [.theme-reel_&]:bg-amber-500/25 [.theme-reel_&]:text-amber-50",
    title:
      "text-foreground [.theme-smoked_&]:text-amber-50 [.theme-spectre_&]:text-amber-50",
    body:
      "text-foreground [.theme-smoked_&]:text-amber-100 [.theme-spectre_&]:text-amber-100",
    icon:
      "text-amber-700 [.theme-smoked_&]:text-amber-300 [.theme-spectre_&]:text-amber-300"
  },
  error: {
    container:
      "border-red-500/30 bg-red-500/10 text-red-900 [.theme-smoked_&]:border-red-500/30 [.theme-smoked_&]:bg-red-500/15 [.theme-smoked_&]:text-red-50 [.theme-spectre_&]:border-red-500/30 [.theme-spectre_&]:bg-red-500/15 [.theme-spectre_&]:text-red-50 [.theme-reel_&]:border-red-500/30 [.theme-reel_&]:bg-red-500/15 [.theme-reel_&]:text-red-50",
    title:
      "text-red-900 [.theme-smoked_&]:text-red-50 [.theme-spectre_&]:text-red-50 [.theme-reel_&]:text-red-50",
    body:
      "text-red-800/90 [.theme-smoked_&]:text-red-100 [.theme-spectre_&]:text-red-100 [.theme-reel_&]:text-red-100",
    icon:
      "text-red-600 [.theme-smoked_&]:text-red-300 [.theme-spectre_&]:text-red-300 [.theme-reel_&]:text-red-300"
  },
  info: {
    container:
      "border-sky-500/30 bg-sky-500/10 text-sky-900 [.theme-smoked_&]:border-sky-500/30 [.theme-smoked_&]:bg-sky-500/15 [.theme-smoked_&]:text-sky-50 [.theme-spectre_&]:border-sky-500/30 [.theme-spectre_&]:bg-sky-500/15 [.theme-spectre_&]:text-sky-50 [.theme-reel_&]:border-sky-500/30 [.theme-reel_&]:bg-sky-500/15 [.theme-reel_&]:text-sky-50",
    title:
      "text-sky-900 [.theme-smoked_&]:text-sky-50 [.theme-spectre_&]:text-sky-50 [.theme-reel_&]:text-sky-50",
    body:
      "text-sky-800/90 [.theme-smoked_&]:text-sky-100 [.theme-spectre_&]:text-sky-100 [.theme-reel_&]:text-sky-100",
    icon:
      "text-sky-600 [.theme-smoked_&]:text-sky-300 [.theme-spectre_&]:text-sky-300 [.theme-reel_&]:text-sky-300"
  }
};

export const Alert = ({
  title,
  children,
  icon,
  variant = "default",
  className,
  dismissible,
  onDismiss
}: AlertProps) => {
  useCommonLanguage();
  return (
  <div
    role="alert"
    className={cn(
      "relative flex items-start gap-3 rounded-md border px-4 py-3 text-sm",
      "[& a]:font-medium [& a]:underline [& a]:underline-offset-2 [& a]:text-current hover:[& a]:opacity-90",
      dismissible && "pr-10",
      variantClasses[variant].container,
      className
    )}
  >
    {icon && <div className={cn("mt-0.5 text-base shrink-0", variantClasses[variant].icon)}>{icon}</div>}
    <div className="min-w-0 flex-1 space-y-1">
      {title && (
        <div className={cn("text-sm font-semibold", variantClasses[variant].title)}>{title}</div>
      )}
      {children && <div className={cn("text-xs", variantClasses[variant].body)}>{children}</div>}
    </div>
    {dismissible && (
      <button
        type="button"
        onClick={onDismiss}
        aria-label={commonText("dismissAlert")}
        className={cn(
          "absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-md",
          "text-current opacity-60 transition-colors",
          "hover:opacity-100 hover:bg-current/10",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current/70",
          "focus-visible:ring-offset-1 focus-visible:ring-offset-transparent"
        )}
      >
        <Icon icon="lucide:x" className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    )}
  </div>
  );
};
